import React, { useEffect, useState } from 'react';
import VyUploadModal from './VyUploadModal';
import { getFileInfo } from '../../services/services';
import { useSelector } from '../../reduxToolkit/typedRedux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { faFile, faFileCirclePlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { createDisplayLink } from '../../services/helper';
import Link from 'next/link';

interface VyFileFieldProps {
  currentAttachments?: string[];
  onChange: (fileIds: string[]) => void;
  compact?: boolean;
  uploadMultiple?: boolean;
  isSignupUpload?: boolean;
  borderColor?: string;
  textColor?: string;
  error?: string;
  disabled?: boolean;
}

export const VyFileField = ({
  currentAttachments,
  onChange,
  compact,
  uploadMultiple,
  isSignupUpload,
  borderColor,
  textColor,
  error,
  disabled,
}: VyFileFieldProps) => {
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const [uploadModal, setUpdloadModal] = useState(false);
  const [attachmentsWithDetails, setAttachmentsWithDetails] = useState<any[]>([]);
  const { t } = useTranslation();

  function handleRemoveFile(fileId: string) {
    if (!currentAttachments) return;
    const updatedAttachments = currentAttachments?.slice()?.filter((file) => file !== fileId);

    onChange(updatedAttachments);
  }

  async function addFileDetails() {
    if (!currentAttachments) return;

    const withDetails = await Promise.all(
      currentAttachments.map(async (fileId) => {
        const res = await getFileInfo(fileId);
        return res.data.file;
      })
    );

    setAttachmentsWithDetails(withDetails);
  }

  useEffect(() => {
    addFileDetails();
  }, [currentAttachments]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        className={`flex flex-wrap gap-2 rounded-md border ${compact ? 'p-2' : 'p-3'}`}
        style={{ borderColor: error ? '#FF1744' : borderColor, color: textColor }}>
        {attachmentsWithDetails.map((file) => (
          <div
            className={`flex relative justify-center items-center max-w-full rounded-md border ${compact ? 'px-1 py-1' : 'px-2 py-1'}`}
            style={{ borderColor: `${borderColor}80` }}>
            <FontAwesomeIcon
              icon={faTrash}
              className={`absolute -top-2 -left-2 p-1 rounded-full transition-colors bg-red-200 text-red-400 hover:text-red-500 ${
                compact ? 'w-3 h-3' : 'w-4 h-4'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => handleRemoveFile(file._id)}
            />
            <Link
              target="_blank"
              className="flex gap-2 items-center"
              href={createDisplayLink(file._id, false, undefined, undefined, undefined, false)}
              style={{ color: textColor }}>
              <FontAwesomeIcon icon={faFile} className={`${compact ? 'h-8' : 'h-10'}`} />
              <p className="truncate">{file.name}</p>
            </Link>
          </div>
        ))}
        {(uploadMultiple || attachmentsWithDetails.length < 1) && (
          <div
            className={`flex gap-2 justify-center items-center p-1 border border-transparent transition-colors ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={() => !disabled && setUpdloadModal(true)}
            style={{ color: `${textColor}aa` }}>
            <FontAwesomeIcon icon={faFileCirclePlus} className={`${compact ? 'h-8' : 'h-10'}`} />
            <p className="">{t('common:upload')}</p>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-[#FF1744] mx-[14px] font-sans">{error}</p>}

      <VyUploadModal
        uploadMultiple={uploadMultiple}
        isSignupUpload={isSignupUpload}
        acceptedTypes="" // allows all file types
        open={uploadModal}
        onClose={() => setUpdloadModal(false)}
        showUploadProgress
        onMultiFilesUpload={(files) => onChange([...(currentAttachments ? currentAttachments : []), ...files.map((file) => file.fileId!)])}
        withUrl={false}
        eventId={event?._id}
      />
    </div>
  );
};
